import { useQuery } from '@tanstack/react-query';

import {
  API_URL,
  axiosInstance,
  CalendarEventLocationAutocompleteRes,
  CalendarEventLocationSuggestion,
} from '../api';

export const CALENDAR_EVENT_LOCATION_AUTOCOMPLETE_MIN_QUERY_LENGTH = 3;

interface UseCalendarEventLocationAutocompleteParams {
  query: string;
  sessionToken?: string | null;
  languageCode?: string;
  regionCode?: string;
  enabled?: boolean;
}

interface UseCalendarEventLocationAutocompleteQueryKey {
  queryKey: [
    'lifestack.calendar.events.locations.autocomplete',
    string,
    string,
    string,
    string | undefined,
  ];
}

const getCalendarEventLocationAutocompleteUrl = () => {
  const path = '/api/v1/calendar/events/locations/autocomplete';
  return API_URL ? `${API_URL}${path}` : path;
};

export const useCalendarEventLocationAutocomplete = ({
  query,
  sessionToken,
  languageCode = 'en',
  regionCode,
  enabled = true,
}: UseCalendarEventLocationAutocompleteParams) => {
  const trimmedQuery = query.trim();

  const autocompleteQuery = useQuery({
    queryKey: [
      'lifestack.calendar.events.locations.autocomplete',
      trimmedQuery,
      sessionToken ?? '',
      languageCode,
      regionCode,
    ],
    queryFn: ({ queryKey }: UseCalendarEventLocationAutocompleteQueryKey) => {
      const [, q, token, lang, region] = queryKey;
      return axiosInstance
        .get<CalendarEventLocationAutocompleteRes>(getCalendarEventLocationAutocompleteUrl(), {
          params: {
            q,
            sessionToken: token,
            languageCode: lang,
            ...(region ? { regionCode: region } : {}),
          },
        })
        .then(({ data }) => data);
    },
    enabled:
      enabled &&
      trimmedQuery.length >= CALENDAR_EVENT_LOCATION_AUTOCOMPLETE_MIN_QUERY_LENGTH &&
      !!sessionToken,
    staleTime: 60_000,
  });

  return {
    suggestions: (autocompleteQuery.data?.suggestions ?? []) as CalendarEventLocationSuggestion[],
    ...autocompleteQuery,
  };
};
